import PropTypes from 'prop-types';
import React from 'react';
import _ from 'underscore';

import DataConnector from 'connect/DataConnector';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import StorableStore from 'samples/StorableStore';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {SampleDetailsByTableName, SampleDetailsWithStageRunSample} from 'workflows/util/StageRunUtil';
import {STAGE_RUN_FULL_SHAPE, STAGE_RUN_SAMPLE_FULL_SHAPE} from 'workflows/WorkflowConstants';

import LegacyAddSamplesModal, {LegacySamplesSource} from './LegacyAddSamplesModal';

function getStageRunSampleIds(stageRunSampleDetailsByTableName: SampleDetailsByTableName): Array<string> {
  const stageRunSampleIds: Array<string> = [];
  for (const tableName of Object.keys(stageRunSampleDetailsByTableName)) {
    const stageRunSampleDetails = stageRunSampleDetailsByTableName[tableName];
    for (const oneSampleDetails of stageRunSampleDetails) {
      stageRunSampleIds.push(oneSampleDetails.stageRunSample.id);
    }
  }
  return stageRunSampleIds;
}

export interface AddStageRunSamplesModalProps {
  title: string;
  enableRowCounts: boolean;
  stageRunSampleDetailsByTableName: SampleDetailsByTableName;
  dropdownIconByTableName: {[tableName: string]: BenchlingIconName};
  onStageRunSamplesSelected: (selectedSampleDetails: Array<SampleDetailsWithStageRunSample>) => void;
  onRequestClose: () => void;
}
interface ConnectedProps {
  // null value indicates that data is still loading
  sources: Array<LegacySamplesSource> | null;
}

class AddStageRunSamplesModalConnector extends DataConnector<AddStageRunSamplesModalProps, ConnectedProps> {
  override stores = [StorableStore];

  enhanceProps(): ConnectedProps {
    const {stageRunSampleDetailsByTableName} = this.props;
    const stageRunSampleIds = getStageRunSampleIds(stageRunSampleDetailsByTableName);

    if (stageRunSampleIds.length === 0) {
      return {
        sources: Object.keys(stageRunSampleDetailsByTableName).map((tableName: string) => ({
          name: tableName,
          icon: this.props.dropdownIconByTableName[tableName],
          sampleDetails: [],
        })),
      };
    }

    const sampleDetailsByStageRunSampleId = this.getOrFetchMultipleOptionalDeprecated(
      ManagedModelType.HYDRATED_STAGE_RUN_SAMPLE_BY_ID,
      stageRunSampleIds,
      true // allOrNothing
    );

    if (_.isEmpty(sampleDetailsByStageRunSampleId)) {
      // Sources are not loaded yet
      return {sources: null};
    }
    const sources: Array<LegacySamplesSource> = [];
    for (const tableName of Object.keys(stageRunSampleDetailsByTableName)) {
      const sampleDetails = stageRunSampleDetailsByTableName[tableName]
        // Filter out inaccessible samples
        .filter((stageRunSampleDetails) => {
          const details = sampleDetailsByStageRunSampleId[stageRunSampleDetails.stageRunSample.id];
          return details.bioentityInfo != null || details.batchInfo != null;
        })
        .map((stageRunSampleDetails) => ({
          ...stageRunSampleDetails,
          ...sampleDetailsByStageRunSampleId[stageRunSampleDetails.stageRunSample.id],
        }));
      sources.push({
        name: tableName,
        icon: this.props.dropdownIconByTableName[tableName],
        sampleDetails,
      });
    }

    return {sources};
  }
}

export const AddStageRunSamplesModal: React.FC<AddStageRunSamplesModalProps & ConnectedProps> = (
  props: AddStageRunSamplesModalProps & ConnectedProps
) => (
  <LegacyAddSamplesModal
    title={props.title}
    enableRowCounts={props.enableRowCounts}
    sources={props.sources}
    onSamplesSelected={props.onStageRunSamplesSelected}
    onRequestClose={props.onRequestClose}
    samplesHaveStageRuns={true}
    omitDefaultSource={true}
  />
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const AddStageRunSamplesModalPropTypes: any = {
  title: PropTypes.string.isRequired,
  enableRowCounts: PropTypes.bool.isRequired,
  // Arrays of samples that may be added, keyed on the source table
  stageRunSampleDetailsByTableName: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        stageRun: STAGE_RUN_FULL_SHAPE.isRequired,
        stageRunSample: STAGE_RUN_SAMPLE_FULL_SHAPE.isRequired,
      }).isRequired
    ).isRequired
  ).isRequired,
  // icons for table names in the source dropdown
  dropdownIconByTableName: PropTypes.objectOf(PropTypes.string),
  onStageRunSamplesSelected: PropTypes.func.isRequired,
  onRequestClose: PropTypes.func.isRequired,

  // Connected
  sources: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired,
      sampleDetails: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
    }).isRequired
  ),
};
AddStageRunSamplesModal.propTypes = AddStageRunSamplesModalPropTypes;

export default AddStageRunSamplesModalConnector.connect(AddStageRunSamplesModal);
